import { useCallback, useEffect, useRef, useState } from "react";
import { createClient, SupabaseClient } from "@supabase/supabase-js";
import type { VacancyApplicationsResponse } from "@/types/vacancy";
import { useAuth0AccessToken } from "@/hooks/useAuth0AccessToken";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL!;
const SUPABASE_ANON = import.meta.env.VITE_SUPABASE_ANON_KEY!;
const EDGE_PATH = "/functions/v1/get-vacancy-applications";

// tiny debounce to avoid spammy refetch on many row updates
function debounce<T extends (...args: any[]) => void>(fn: T, ms: number) {
  let t: number | undefined;
  return (...args: Parameters<T>) => {
    if (t) window.clearTimeout(t);
    t = window.setTimeout(() => fn(...args), ms);
  };
}

export function useVacancyApplications(vacancyId: string) {
  const [data, setData] = useState<VacancyApplicationsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setErr] = useState<unknown>(null);

  // supabase client (browser-safe)
  const sbRef = useRef<SupabaseClient | null>(null);
  if (!sbRef.current) {
    sbRef.current = createClient(SUPABASE_URL, SUPABASE_ANON);
  }
  const supabase = sbRef.current!;
  const getAccessToken = useAuth0AccessToken();

  const buildEndpoint = useCallback(
    (syncSnapshot: boolean) => {
      if (!vacancyId) return null;
      const qs = new URLSearchParams({
        vacancy: vacancyId,
        syncSnapshot: syncSnapshot ? "true" : "false",
      });
      return `${SUPABASE_URL}${EDGE_PATH}?${qs.toString()}`;
    },
    [vacancyId]
  );

  const buildHeaders = useCallback(async () => {
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (SUPABASE_ANON) {
      headers.apikey = SUPABASE_ANON;
      headers.Authorization = `Bearer ${SUPABASE_ANON}`;
    }
    headers["x-auth-token"] = `Bearer ${await getAccessToken()}`;
    return headers;
  }, [getAccessToken]);

  const fetchNow = useCallback(async () => {
    const endpoint = buildEndpoint(true);
    if (!endpoint) return;
    setLoading(true);
    setErr(null);

    // IMPORTANT: don't abort on unmount; just skip state updates after unmount
    let mounted = true;
    try {
      const headers = await buildHeaders();
      const res = await fetch(endpoint, {
        method: "GET",
        headers,
        credentials: "omit",
        cache: "no-store",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = (await res.json()) as VacancyApplicationsResponse;
      if (mounted) setData(json);
    } catch (e) {
      if (mounted) setErr(e);
    } finally {
      if (mounted) setLoading(false);
    }
    // return a cleanup function for callers that want to mark unmounted
    return () => {
      mounted = false;
    };
  }, [buildEndpoint, buildHeaders]);

  // initial fetch (no abort on unmount; we rely on mounted flag)
  useEffect(() => {
    if (!vacancyId) return;
    let cleanup: (() => void) | undefined;
    (async () => {
      cleanup = await fetchNow();
    })();
    return () => {
      if (cleanup) cleanup();
    };
  }, [vacancyId, fetchNow]);

  // realtime → debounced refetch
  useEffect(() => {
    if (!vacancyId) return;

    const debouncedRefetch = debounce(() => {
      const realtimeEndpoint = buildEndpoint(false);
      if (!realtimeEndpoint) {
        return;
      }
      (async () => {
        try {
          const headers = await buildHeaders();
          const r = await fetch(realtimeEndpoint, {
            method: "GET",
            headers,
            credentials: "omit",
            cache: "no-store",
          });
          if (!r.ok) {
            throw new Error(`HTTP ${r.status}`);
          }
          const json = (await r.json()) as VacancyApplicationsResponse;
          setData(json);
        } catch (e) {
          console.warn("[realtime refetch] failed:", e);
        }
      })();
    }, 250);

    const channel = supabase
      .channel(`apps-${vacancyId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "applications", filter: `vacancy_id=eq.${vacancyId}` },
        () => debouncedRefetch()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [supabase, vacancyId, buildEndpoint, buildHeaders]);

  const refetch = useCallback(() => {
    // fire and forget; no spinner
    const endpoint = buildEndpoint(false);
    if (!endpoint) return;
    (async () => {
      try {
        const headers = await buildHeaders();
        const r = await fetch(endpoint, {
          method: "GET",
          headers,
          credentials: "omit",
          cache: "no-store",
        });
        if (!r.ok) {
          throw new Error(`HTTP ${r.status}`);
        }
        const json = (await r.json()) as VacancyApplicationsResponse;
        setData(json);
      } catch (e) {
        setErr(e);
      }
    })();
  }, [buildEndpoint, buildHeaders]);

  return { data, loading, error, refetch };
}
